import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Default pattern: numeric placeholders only ({{1}}, {{2}}, ...), as used by WhatsApp
// templates. Callers with a differently-shaped template (named placeholders, e.g.
// ContentTemplateParser/WhatsAppTemplateParser/ReminderTemplateFields) pass their own
// variablePattern below.
export const NUMERIC_VARIABLE_PATTERN = /\{\{\d+\}\}/g;

/**
 * Pure variable-token extraction, usable outside a component's setup() (unlike the
 * composable below, which needs useI18n()) — see e.g. the WhatsApp flow tree's
 * templateVariables.js, which validates node content well outside any component.
 *
 * @param {string} body
 * @param {RegExp} pattern
 * @returns {string[]} de-duplicated tokens with braces stripped, e.g. ['1', '2']
 */
export function extractVariableTokens(
  body,
  pattern = NUMERIC_VARIABLE_PATTERN
) {
  if (!body) return [];
  const matches = body.match(pattern);
  if (!matches) return [];
  return [...new Set(matches)].map(v => v.replace(/[{}]/g, ''));
}

/**
 * A variable with no source picked, or a static value left blank, goes out empty and
 * Meta rejects the send with (#132000).
 *
 * @param {Object} selections variable key => picked source ('__static__' for a static value)
 * @param {Object} mappings variable key => mapped value
 * @param {string} varKey
 * @returns {boolean}
 */
export function isVariableUnmapped(selections, mappings, varKey) {
  const selection = selections[varKey];
  if (!selection) return true;
  return selection === '__static__' && !mappings[varKey]?.trim();
}

/**
 * Composable for template variable mapping logic shared across
 * WhatsAppCampaignForm, AddStepDialog, WhatsAppFlowForm, and ContentTemplateParser.
 */
export function useTemplateVariableMapping({
  i18nBaseKey,
  variablePattern = NUMERIC_VARIABLE_PATTERN,
  contact = null,
  contactAttributes = null,
  webhookVariables = null,
  webhookVariableLabelKey = null,
  includeAppointmentVariables = false,
}) {
  const { t } = useI18n();

  const variableMappingOptions = computed(() => {
    const contactObj = contact?.value ?? null;

    const contactFields = [
      {
        value: '{{ contact.name }}',
        label: t(`${i18nBaseKey}.CONTACT_NAME`),
        resolved: contactObj?.name,
      },
      {
        value: '{{ contact.email }}',
        label: t(`${i18nBaseKey}.CONTACT_EMAIL`),
        resolved: contactObj?.email,
      },
      {
        value: '{{ contact.phone_number }}',
        label: t(`${i18nBaseKey}.CONTACT_PHONE`),
        resolved: contactObj?.phoneNumber || contactObj?.phone_number,
      },
      {
        value: '{{ contact.first_name }}',
        label: t(`${i18nBaseKey}.CONTACT_FIRST_NAME`),
        resolved: contactObj?.firstName || contactObj?.first_name,
      },
      {
        value: '{{ contact.last_name }}',
        label: t(`${i18nBaseKey}.CONTACT_LAST_NAME`),
        resolved: contactObj?.lastName || contactObj?.last_name,
      },
    ];

    const options = contactObj
      ? contactFields.filter(f => f.resolved)
      : contactFields;

    // Custom attributes from store
    const attrs = contactAttributes?.value || [];
    const customAttrs =
      contactObj?.customAttributes || contactObj?.custom_attributes || {};
    attrs.forEach(attr => {
      if (!contactObj || customAttrs[attr.attributeKey]) {
        options.push({
          value: `{{ contact.custom_attribute.${attr.attributeKey} }}`,
          label: `${t(`${i18nBaseKey}.CUSTOM_ATTRIBUTE`)} - ${attr.attributeDisplayName}`,
        });
      }
    });

    // Webhook variables
    const webhookVars = webhookVariables?.value || [];
    webhookVars.forEach(varName => {
      const label = webhookVariableLabelKey
        ? t(webhookVariableLabelKey, { varName })
        : `Webhook - ${varName}`;
      options.push({
        value: `{{ contact.custom_attribute.${varName} }}`,
        label,
      });
    });

    if (includeAppointmentVariables) {
      const appointmentFields = [
        { value: '{{ appointment.date }}', labelKey: 'APPOINTMENT_DATE' },
        { value: '{{ appointment.time }}', labelKey: 'APPOINTMENT_TIME' },
        {
          value: '{{ appointment.datetime }}',
          labelKey: 'APPOINTMENT_DATETIME',
        },
        { value: '{{ appointment.link }}', labelKey: 'APPOINTMENT_LINK' },
        {
          value: '{{ appointment.time_remaining }}',
          labelKey: 'APPOINTMENT_TIME_REMAINING',
        },
      ];

      appointmentFields.forEach(({ value, labelKey }) => {
        options.push({
          value,
          label: t(`${i18nBaseKey}.${labelKey}`),
        });
      });
    }

    options.push({
      value: '__static__',
      label: t(`${i18nBaseKey}.STATIC_VALUE`),
    });
    return options;
  });

  const extractTemplateVariables = body =>
    extractVariableTokens(body, variablePattern);

  const isStaticMode = (selections, varKey) =>
    selections[varKey] === '__static__';

  const handleVariableSelect = (selectionsRef, mappingsRef, varKey, val) => {
    selectionsRef.value = { ...selectionsRef.value, [varKey]: val };
    mappingsRef.value = {
      ...mappingsRef.value,
      [varKey]: val === '__static__' ? '' : val,
    };
  };

  const getOptionLabel = value => {
    if (!value || value === '__static__') return '';
    const option = variableMappingOptions.value.find(
      opt => opt.value === value
    );
    return option ? option.label : value;
  };

  const getPreviewContent = (content, selections, mappings) => {
    if (!content) return '';

    Object.entries(selections).forEach(([varKey, selection]) => {
      if (!selection) return;
      let displayValue;
      if (selection === '__static__') {
        const staticText = mappings[varKey] || `{{${varKey}}}`;
        displayValue = `[Static - ${staticText}]`;
      } else {
        displayValue = `[${getOptionLabel(selection)}]`;
      }
      content = content.replace(
        new RegExp(`\\{\\{${varKey}\\}\\}`, 'g'),
        displayValue
      );
    });
    return content;
  };

  const resolveContactValue = (liquidExpression, contactObj) => {
    if (!contactObj || !liquidExpression) return null;

    const fieldMap = {
      '{{ contact.name }}': contactObj.name,
      '{{ contact.email }}': contactObj.email,
      '{{ contact.phone_number }}':
        contactObj.phoneNumber || contactObj.phone_number,
      '{{ contact.first_name }}': contactObj.firstName || contactObj.first_name,
      '{{ contact.last_name }}': contactObj.lastName || contactObj.last_name,
    };

    if (fieldMap[liquidExpression] !== undefined) {
      return fieldMap[liquidExpression];
    }

    const customAttrMatch = liquidExpression.match(
      /\{\{\s*contact\.custom_attribute\.(\S+)\s*\}\}/
    );
    if (customAttrMatch) {
      const attrKey = customAttrMatch[1];
      const attrs =
        contactObj.customAttributes || contactObj.custom_attributes || {};
      return attrs[attrKey] ?? null;
    }

    return null;
  };

  return {
    variableMappingOptions,
    extractTemplateVariables,
    isStaticMode,
    handleVariableSelect,
    getOptionLabel,
    getPreviewContent,
    resolveContactValue,
  };
}
